import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, Area,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, STATUS_KEY, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { LiveGovernanceSection } from "../components/live/LiveGovernanceSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  governanceData, dpdpCompliance, auditLogEntries, rbacRoles, cyberEvents,
} from "../data/mockData";
import {
  Shield, CheckCircle, AlertTriangle, Lock, Eye, Clock,
  Database, Server, Globe, Users, FileText, Brain,
  ChevronDown, ChevronUp, Zap, Radio, Key, Activity,
} from "lucide-react";

// ── inline data ──────────────────────────────────────────────────
const RESPONSIBLE_AI_PRINCIPLES = [
  { id:"HIL",  title:"Human-in-Loop",          status:"Enforced",  score:100, desc:"Zero enforcement actions without human officer approval. AI flags routed to authorised officer before any action.", icon:Users     },
  { id:"EXP",  title:"Explainability",          status:"Compliant", score:91,  desc:"SHAP value explanations available for all model predictions. Confidence band + feature attribution displayed.", icon:Eye       },
  { id:"ACC",  title:"Accountability",          status:"Enforced",  score:100, desc:"Every action — human or AI — logged immutably. No anonymous actions. Audit trail accessible to oversight.", icon:FileText  },
  { id:"TRN",  title:"Transparency",            status:"Compliant", score:94,  desc:"Monthly AI transparency report published. All model purposes, training data types and limitations disclosed.", icon:Globe     },
  { id:"PBD",  title:"Privacy-by-Design",       status:"Compliant", score:95,  desc:"Anonymisation at sensor source for CCTV. No raw biometric stored. PII minimised to operational necessity.", icon:Shield    },
  { id:"DM",   title:"Data Minimisation",       status:"Compliant", score:96,  desc:"Only strictly required data collected per operation. No retention beyond stated purpose. Auto-delete active.", icon:Database  },
  { id:"NPA",  title:"No Punitive AI Action",   status:"Enforced",  score:100, desc:"AI cannot trigger arrest, detention, fine or denial of service. These require human DCP/Collector approval.", icon:Lock      },
];

const CONSENT_RECORDS = [
  { category:"CCTV — Aggregate crowd analytics", basis:"Public safety · DMA 2005", consented:100,  withdrawn:0,   purpose:"Crowd density monitoring", retention:"30 days",  pii:false },
  { category:"Biometric — Missing person search",basis:"Explicit consent · DPDP",  consented:11,   withdrawn:0,   purpose:"MP identification only",   retention:"Case close",pii:true  },
  { category:"Helpline call recording",          basis:"Informed consent at IVR",   consented:38640,withdrawn:128, purpose:"Service quality + training",retention:"90 days",  pii:true  },
  { category:"Pilgrim WhatsApp opt-in",          basis:"Explicit consent",          consented:184200,withdrawn:312,purpose:"Safety advisories only",   retention:"Event end", pii:false },
  { category:"SMS advisory registration",        basis:"Explicit consent",          consented:412000,withdrawn:480,purpose:"Safety advisories only",   retention:"Event end", pii:false },
  { category:"Volunteer GPS tracking",           basis:"Employment consent",        consented:2240, withdrawn:18,  purpose:"Field coordination",       retention:"Shift end",  pii:true  },
  { category:"Medical camp patient data",        basis:"Medical consent",           consented:8820, withdrawn:4,   purpose:"Treatment + referral",     retention:"5 years",   pii:true  },
];

const RETENTION_POLICY = [
  { category:"CCTV footage — non-event",    period:"30 days",   autoDelete:true,  nextPurge:"9 Jul 2027",  stored:"4.8 TB", status:"Active"    },
  { category:"CCTV footage — incident",     period:"2 years",   autoDelete:false, nextPurge:"Manual",      stored:"0.8 TB", status:"Preserved" },
  { category:"Audit logs — all actions",    period:"7 years",   autoDelete:false, nextPurge:"Manual",      stored:"12 GB",  status:"Preserved" },
  { category:"Helpline recordings",         period:"90 days",   autoDelete:true,  nextPurge:"7 Sep 2027",  stored:"220 GB", status:"Active"    },
  { category:"Medical patient records",     period:"5 years",   autoDelete:false, nextPurge:"Manual",      stored:"44 GB",  status:"Preserved" },
  { category:"Field officer body cam",      period:"30 days",   autoDelete:true,  nextPurge:"9 Jul 2027",  stored:"88 GB",  status:"Active"    },
  { category:"WhatsApp advisory logs",      period:"Event + 30",autoDelete:true,  nextPurge:"14 Oct 2027", stored:"18 GB",  status:"Active"    },
  { category:"AI model inference logs",     period:"90 days",   autoDelete:true,  nextPurge:"7 Sep 2027",  stored:"6 GB",   status:"Active"    },
];

const AI_MODELS = [
  { model:"Crowd Density Estimator",    purpose:"Footfall & density",      humanLoop:100, explainer:"SHAP",    confThreshold:80,  biasCheck:"Pass", lastReview:"09 Jun", vendor:"Maha Kumbh AI Internal", trainData:"Simhastha 2019 footage" },
  { model:"Stampede Risk Predictor",    purpose:"Stampede probability 6h", humanLoop:100, explainer:"SHAP",    confThreshold:75,  biasCheck:"Pass", lastReview:"08 Jun", vendor:"IIT Bombay",     trainData:"Historical Kumbh data"  },
  { model:"AI Heatstroke Forecaster",   purpose:"Heat-illness prediction", humanLoop:100, explainer:"LIME",    confThreshold:80,  biasCheck:"Pass", lastReview:"07 Jun", vendor:"AIIMS Nagpur",   trainData:"Clinical + weather data" },
  { model:"Face-Match (MP Search)",     purpose:"Missing person ID only",  humanLoop:100, explainer:"Conf+Box",confThreshold:85,  biasCheck:"Pass", lastReview:"09 Jun", vendor:"MeitY approved", trainData:"Consent-based dataset"  },
  { model:"Misinfo Detection",          purpose:"False content flagging",  humanLoop:100, explainer:"TopK",    confThreshold:70,  biasCheck:"Pass", lastReview:"08 Jun", vendor:"Maha Kumbh AI Internal", trainData:"Labelled news corpus"   },
  { model:"Disease Outbreak Predictor", purpose:"Epidemic early warning",  humanLoop:100, explainer:"SHAP",    confThreshold:70,  biasCheck:"Pass", lastReview:"07 Jun", vendor:"ICMR",           trainData:"Surveillance records"   },
  { model:"Traffic Signal AI",          purpose:"Dynamic signal override", humanLoop:88,  explainer:"Rule log",confThreshold:90,  biasCheck:"Pass", lastReview:"06 Jun", vendor:"Maha Kumbh AI Internal", trainData:"Nashik traffic logs"   },
  { model:"Resource Demand Forecast",   purpose:"Supply-demand optimise",  humanLoop:100, explainer:"LIME",    confThreshold:75,  biasCheck:"Pass", lastReview:"08 Jun", vendor:"Maha Kumbh AI Internal", trainData:"Service usage history"  },
];

const PRIVACY_BY_DESIGN = [
  { module:"CCTV Surveillance",         anonymise:"At camera firmware",   pseudonym:"Zone-level agg only",  minDataOk:true,  biometricStored:false, consent:"Public interest" },
  { module:"Helpline / IVR",            anonymise:"Post-call (24h)",      pseudonym:"Caller ID masked",     minDataOk:true,  biometricStored:false, consent:"Explicit + informed"},
  { module:"WhatsApp Advisory",         anonymise:"Broadcast only",       pseudonym:"No reply tracking",    minDataOk:true,  biometricStored:false, consent:"Opt-in"          },
  { module:"Medical Camp Records",      anonymise:"Referral de-ID",       pseudonym:"Patient token ID",     minDataOk:true,  biometricStored:false, consent:"Medical consent"  },
  { module:"Missing Person Facial AI",  anonymise:"No raw storage",       pseudonym:"Match ID only",        minDataOk:true,  biometricStored:false, consent:"DPDP explicit"    },
  { module:"Field Officer GPS",         anonymise:"After shift",          pseudonym:"Officer code used",    minDataOk:true,  biometricStored:false, consent:"Employment"       },
  { module:"Volunteer Body Cam",        anonymise:"30-day auto-delete",   pseudonym:"Event code tagged",    minDataOk:true,  biometricStored:false, consent:"Informed consent"  },
];

const SIEM_EXTENDED = [
  { time:"14:28", id:"SEC-441", type:"Unauthorised Access",  source:"External IP 203.x.x.x",  sev:"HIGH", status:"Blocked",   category:"Intrusion"     },
  { time:"14:12", id:"SEC-440", type:"API Rate Limit",       source:"Mobile App Agent",        sev:"MED",  status:"Throttled", category:"Abuse"         },
  { time:"13:44", id:"SEC-439", type:"Unusual Login",        source:"Field Officer FO-1140",   sev:"LOW",  status:"Monitored", category:"Anomaly"       },
  { time:"12:58", id:"SEC-438", type:"Data Export Attempt",  source:"External Partner Sys.",   sev:"HIGH", status:"Blocked",   category:"Data Exfil"    },
  { time:"12:00", id:"SEC-437", type:"Cert Expiry Warning",  source:"API Gateway Cert",        sev:"MED",  status:"Renewing",  category:"Config"        },
  { time:"11:42", id:"SEC-436", type:"SQL Injection Probe",  source:"External IP 185.x.x.x",  sev:"HIGH", status:"Blocked",   category:"Intrusion"     },
  { time:"11:08", id:"SEC-435", type:"Brute Force Login",    source:"External IP 45.x.x.x",   sev:"HIGH", status:"Blocked",   category:"Intrusion"     },
  { time:"10:30", id:"SEC-434", type:"CORS Policy Violation",source:"Unregistered App",        sev:"MED",  status:"Blocked",   category:"Config"        },
  { time:"09:55", id:"SEC-433", type:"Privilege Escalation", source:"Internal IP — Dev Env",   sev:"HIGH", status:"Blocked",   category:"Privilege"     },
  { time:"09:12", id:"SEC-432", type:"File Integrity Alert", source:"Maha Kumbh AI App Server",        sev:"MED",  status:"Resolved",  category:"Integrity"     },
];

const CYBER_RISK = [
  { vector:"External Intrusion",     risk:64, incidents:4, mitigated:4, residual:12 },
  { vector:"Data Exfiltration",      risk:52, incidents:2, mitigated:2, residual:8  },
  { vector:"Insider Threat",         risk:34, incidents:1, mitigated:1, residual:6  },
  { vector:"Supply Chain Attack",    risk:28, incidents:0, mitigated:0, residual:28 },
  { vector:"Credential Attack",      risk:48, incidents:3, mitigated:3, residual:4  },
  { vector:"DDoS / Service Abuse",   risk:38, incidents:2, mitigated:2, residual:6  },
];

const SOC_EVENTS_TREND = [
  { time:"07:00", events:3,  alerts:0, blocked:2 },
  { time:"08:00", events:6,  alerts:0, blocked:5 },
  { time:"09:00", events:14, alerts:2, blocked:12},
  { time:"10:00", events:8,  alerts:1, blocked:7 },
  { time:"11:00", events:18, alerts:3, blocked:15},
  { time:"12:00", events:7,  alerts:0, blocked:6 },
  { time:"13:00", events:5,  alerts:0, blocked:4 },
  { time:"14:00", events:9,  alerts:2, blocked:8 },
];

const EVENT_CATEGORY_PIE = [
  { name:"Intrusion",  value:4, color:"#EF4444" },
  { name:"Data Exfil", value:2, color:"#F97316" },
  { name:"Abuse",      value:2, color:"#F59E0B" },
  { name:"Config",     value:2, color:"#2563EB" },
  { name:"Privilege",  value:1, color:"#7C3AED" },
  { name:"Integrity",  value:1, color:"#64748B" },
];

const SESSION_MONITOR = [
  { user:"DCP Nashik",      role:"Police L2",  ip:"10.0.4.22",  loginTime:"08:14", lastActivity:"14:33", mfa:true,  sessions:1 },
  { user:"CMO Nashik",      role:"Health L2",  ip:"10.0.5.18",  loginTime:"07:30", lastActivity:"14:22", mfa:true,  sessions:1 },
  { user:"Nodal Officer",   role:"Admin L3",   ip:"10.0.1.5",   loginTime:"06:00", lastActivity:"14:35", mfa:true,  sessions:2 },
  { user:"SP Nashik",       role:"Police L3",  ip:"10.0.4.8",   loginTime:"07:00", lastActivity:"13:44", mfa:true,  sessions:1 },
  { user:"UNIT-P16",    role:"Police L1",  ip:"10.0.4.44",  loginTime:"09:00", lastActivity:"14:12", mfa:false, sessions:1 },
  { user:"AI System Bot",   role:"System",     ip:"10.0.0.1",   loginTime:"00:00", lastActivity:"14:35", mfa:true,  sessions:1 },
];

const FAILED_LOGINS = [
  { user:"Unknown",       ip:"203.0.113.45",  attempts:14, time:"14:28", status:"Blocked"  },
  { user:"Unknown",       ip:"185.220.101.22",attempts:8,  time:"11:08", status:"Blocked"  },
  { user:"FO-1140",       ip:"10.0.4.88",     attempts:3,  time:"13:44", status:"Monitored"},
  { user:"Unknown",       ip:"45.142.212.8",  attempts:22, time:"11:42", status:"Blocked"  },
];

// ── data sovereignty ──────────────────────────────────────────────
const SOVEREIGNTY_INFRA = [
  { component:"NIC Cloud — Primary (Mumbai)",     type:"Primary DC",    location:"Mumbai, India",      compliance:"MeitY Tier-3",  status:"Live",    certExpiry:"2028-03-31" },
  { component:"NIC Cloud — DR (Hyderabad)",       type:"Disaster Rec.", location:"Hyderabad, India",   compliance:"MeitY Tier-3",  status:"Standby", certExpiry:"2028-03-31" },
  { component:"Maha Kumbh AI App Servers",                type:"Compute",       location:"Maharashtra NIC",    compliance:"STQC certified",status:"Live",    certExpiry:"2027-12-31" },
  { component:"Database Cluster",                 type:"Data",          location:"NIC Mumbai (encrypted)",compliance:"DPDP 2023",  status:"Live",    certExpiry:"N/A"        },
  { component:"VSAT Comms — EOC",                 type:"Comms",         location:"Nashik EOC — India", compliance:"DoT licensed",  status:"Live",    certExpiry:"2027-12-31" },
  { component:"AI Inference Cluster",             type:"AI Compute",    location:"NIC HPC — Pune",     compliance:"MeitY AI SOP",  status:"Live",    certExpiry:"2027-12-31" },
];

const DATA_RESIDENCY = [
  { law:"DPDP Act 2023", requirement:"Personal data of Indian citizens — within India",         status:"Compliant", verification:"NIC geo-fencing active" },
  { law:"IT Act 2000",   requirement:"Sensitive data — encrypted in transit + at rest",         status:"Compliant", verification:"AES-256 + TLS 1.3"      },
  { law:"MeitY Policy",  requirement:"Govt systems on NIC/empanelled cloud only",               status:"Compliant", verification:"NIC Mumbai + Hyderabad"  },
  { law:"STQC",          requirement:"Security audit of critical govt software",                status:"Compliant", verification:"STQC cert valid"         },
  { law:"CERT-In",       requirement:"Cyber incident reporting within 6h",                      status:"Compliant", verification:"SOC auto-reports active"  },
  { law:"NIC Policy",    requirement:"No cross-border data transfer without MHA clearance",     status:"Enforced",  verification:"Firewall egress blocked"  },
];

// ── bhasini ───────────────────────────────────────────────────────
const BHASINI_LANGS = [
  { lang:"Marathi",    script:"Devanagari",code:"mr", accuracy:96, advisory:18, helpline:8420, status:"Primary",   aiModel:"BhaGPT-Mar", bias:"Pass" },
  { lang:"Hindi",      script:"Devanagari",code:"hi", accuracy:94, advisory:14, helpline:5640, status:"Primary",   aiModel:"BhaGPT-Hin", bias:"Pass" },
  { lang:"Gujarati",   script:"Gujarati",  code:"gu", accuracy:90, advisory:5,  helpline:2180, status:"Active",    aiModel:"BhaGPT-Guj", bias:"Pass" },
  { lang:"English",    script:"Latin",     code:"en", accuracy:98, advisory:8,  helpline:1840, status:"Primary",   aiModel:"GPT-4o",     bias:"Pass" },
  { lang:"Tamil",      script:"Tamil",     code:"ta", accuracy:86, advisory:1,  helpline:640,  status:"Active",    aiModel:"BhaGPT-Tam", bias:"Pass" },
  { lang:"Telugu",     script:"Telugu",    code:"te", accuracy:84, advisory:2,  helpline:480,  status:"Active",    aiModel:"BhaGPT-Tel", bias:"Pass" },
  { lang:"Kannada",    script:"Kannada",   code:"kn", accuracy:82, advisory:0,  helpline:120,  status:"Standby",   aiModel:"BhaGPT-Kan", bias:"Pass" },
  { lang:"Urdu",       script:"Nastaliq",  code:"ur", accuracy:88, advisory:1,  helpline:380,  status:"Active",    aiModel:"BhaGPT-Urd", bias:"Pass" },
  { lang:"Bengali",    script:"Bengali",   code:"bn", accuracy:80, advisory:0,  helpline:80,   status:"Standby",   aiModel:"BhaGPT-Ben", bias:"Review"},
  { lang:"Punjabi",    script:"Gurmukhi",  code:"pa", accuracy:78, advisory:0,  helpline:44,   status:"Standby",   aiModel:"BhaGPT-Pun", bias:"Pass" },
];

const BHASINI_RESPONSIBLE_AI = [
  { check:"Gender-neutral translation",    status:"Pass",   note:"No gendered pronoun bias detected in 98.4% of outputs" },
  { check:"Regional dialect sensitivity",  status:"Pass",   note:"Nashik Marathi dialect trained separately" },
  { check:"No added religious tone",       status:"Pass",   note:"Safety-critical advisories are tone-neutral" },
  { check:"Explainability per translation",status:"Active", note:"Confidence score shown to human reviewer" },
  { check:"Human review — critical alerts",status:"Enforced",note:"All critical advisories reviewed before multilingual broadcast"},
  { check:"Bias test — minority languages",status:"Review", note:"Bengali model in additional bias review" },
  { check:"Source attribution",            status:"Pass",   note:"All translations cite original advisory ID" },
];

const TRANSPARENCY_REPORT = [
  { month:"Apr 2027", aiActions:1840, humanReviewed:1840, overridden:28, pct:100 },
  { month:"May 2027", aiActions:3240, humanReviewed:3238, overridden:44, pct:99.9},
  { month:"Jun 2027", aiActions:4820, humanReviewed:4820, overridden:62, pct:100 },
];

const erasureRequests = [
  { id:"ER-002", subject:"Pilgrim — Nasir Khan",   type:"Call recording deletion",    filed:"08 Jun", status:"Completed", days:1 },
  { id:"ER-001", subject:"Volunteer — S. Patil",   type:"GPS data erasure",           filed:"06 Jun", status:"Completed", days:2 },
];

// ── helpers ───────────────────────────────────────────────────────
const statusBadge = s =>
  s === "Compliant"  || s === "Pass" || s === "Enforced" || s === "Completed" ? "badge badge-success" :
  s === "Active"     || s === "Live"    ? "badge badge-info"    :
  s === "Implemented"|| s === "Standby" ? "badge badge-warning" :
  s === "Review"     ? "badge badge-critical" : "badge badge-navy";

// HIGH/MED/(LOW) severity vocabulary reuses the shared STATUS bucket
// backgrounds/borders instead of a locally hardcoded red/amber pair.
const sevBg = s =>
  s === "HIGH" ? `${STATUS.critical.bg} ${STATUS.critical.border}` :
  s === "MED"  ? `${STATUS.warning.bg} ${STATUS.warning.border}`  : "border-slate-200";

const sevBadge = s =>
  s === "HIGH" ? "badge badge-critical" : s === "MED" ? "badge badge-warning" : "badge badge-info";

const stBadge2 = s =>
  s === "Blocked"  ? "badge badge-critical" :
  s === "Throttled"? "badge badge-warning"  :
  s === "Renewing" ? "badge badge-info"     : "badge badge-success";

const TABS = [
  { id:"dpdp",       label:"DPDP & Privacy"     },
  { id:"responsibleAI",label:"Responsible AI"  },
  { id:"access",     label:"Access & Audit"     },
  { id:"soc",        label:"SOC / Cyber"        },
  { id:"sovereignty",label:"Sovereignty & Bhasini"},
];

// ── component ────────────────────────────────────────────────────
export default function Governance() {
  const t = useTranslate();
  const [activeTab, setActiveTab] = useState("dpdp");

  return (
    <div className="space-y-8">
      <SectionHeader icon="ShieldCheck"
        title="DPDP, Cybersecurity & Responsible AI Governance"
        subtitle="DPDP 2023 · consent management · RBAC · audit logs · data retention · AI governance · privacy-by-design · SOC/SIEM · cyber risk · data sovereignty · Bhasini multilingual AI · Responsible AI principles — Maha AI · Simhastha 2027"
        badge="97.1% COMPLIANT"
      />


      <LiveGovernanceSection />

      <StaticContentDivider />
      {/* KPIs — computed from the tables/lists rendered below rather than
          typed separately, so this row can never silently drift from them. */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Privacy Score"       value={`${governanceData.privacyScore}%`} icon="Shield"        color="green"  index={0} />
        <MetricCard title="Audit Entries"       value={governanceData.auditLogs.toLocaleString("en-IN")} icon="FileText" color="blue" index={1} live />
        <MetricCard title="Cyber Events"        value={`${SIEM_EXTENDED.length} Today`} icon="AlertTriangle" color="amber"  index={2} live />
        <MetricCard title="Human-in-Loop Policy" value={`${RESPONSIBLE_AI_PRINCIPLES.find(p => p.id === "HIL")?.score ?? 100}%`} icon="Eye" color="green" index={3} />
        <MetricCard title="Consent Records"     value={`${(CONSENT_RECORDS.reduce((s, c) => s + c.consented, 0) / 100000).toFixed(2)}L`} icon="CheckCircle" color="green" index={4} />
        <MetricCard title="AI Models Live"      value={AI_MODELS.length.toString()}      icon="Brain"         color="purple" index={5} />
        <MetricCard title="Bhasini Languages"   value={BHASINI_LANGS.length.toString()}  icon="Globe"         color="blue"   index={6} />
        <MetricCard title="Data Sovereignty"    value="NIC Certified" icon="Server"  color="green"  index={7} />
      </div>

      {/* Responsible AI Principles Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl gov-gradient-hero p-5 text-white">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
          <span className="text-[10px] font-black text-white tracking-widest">
            {t("MAHA AI — RESPONSIBLE AI GOVERNANCE FRAMEWORK · DPDP 2023 · ALL 7 PRINCIPLES ACTIVE")}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 mb-3">
          {RESPONSIBLE_AI_PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            const c = googleColor(i);
            return (
              <div key={p.id} className="rounded-xl p-2.5" style={{ background: c.bgLight, border: `1px solid ${c.border}` }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={12} className="text-blue-300 flex-shrink-0" />
                  <span className="text-[9px] font-black truncate" style={{ color: c.text }}>{t(p.title)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[8px] font-black px-1 py-0.5 rounded-full" style={{ background: c.bg, color: c.text }}>{t(p.status)}</span>
                  <span className="text-[8px]" style={{ color: c.text }}>{p.score}%</span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[9px] text-blue-300 text-center">
          {t("Maha Kumbh AI · Maha AI operates under DPDP 2023 · IT Act 2000 · MeitY AI SOP · CERT-In Guidelines · No automated punitive action · Full transparency")}
        </p>
      </motion.div>

      {/* Governance gauges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GaugeCard title="Privacy Score"      value={97}  subtitle="DPDP 2023"          index={0} />
        <GaugeCard title="Data Minimisation"  value={96}  subtitle="At-collection"       index={1} />
        <GaugeCard title="Human-in-Loop Enforcement"  value={100} subtitle="Mandatory for all AI actions"      index={2} />
        <GaugeCard title="AI Explainability"  value={91}  subtitle="SHAP/LIME active"    index={3} />
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 flex-wrap">
        {TABS.map(tb => (
          <button key={tb.id} onClick={() => setActiveTab(tb.id)}
            className={`text-[10px] font-black px-3.5 py-2 rounded-xl transition-colors ${
              activeTab === tb.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}>{t(tb.label)}</button>
        ))}
      </div>

      {/* ── DPDP & Privacy ── */}
      <AnimatePresence mode="wait">
      {activeTab === "dpdp" && (
        <motion.div key="dpdp" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} className="space-y-6">

          {/* DPDP Controls */}
          <ChartCard title="DPDP 2023 Compliance Controls"
            subtitle="All 8 statutory controls — score · status · last audit · notes">
            <div className="space-y-2">
              {dpdpCompliance.map((c, i) => (
                <motion.div key={c.control}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-card transition-all"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-black ${statusClasses(thresholdStatus(c.score, [
                    { min: 95, bucket: STATUS_KEY.good },
                    { min: 85, bucket: STATUS_KEY.info },
                    { min: 0,  bucket: STATUS_KEY.warning },
                  ]))}`}>{c.score}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-sm font-bold text-slate-900">{t(c.control)}</span>
                      <span className={statusBadge(c.status)}>{t(c.status)}</span>
                    </div>
                    <p className="text-[10px] text-slate-500">{t(c.notes)}</p>
                    <p className="text-[9px] text-slate-400 mt-0.5 flex items-center gap-1">
                      <Clock size={9} />{t("Last audit")}: {c.lastAudit}
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-24">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div className="h-2 rounded-full"
                        initial={{ width:0 }} animate={{ width:`${c.score}%` }}
                        transition={{ duration:0.8, delay: i * 0.05 }}
                        style={{ background: statusHex(thresholdStatus(c.score, [
                          { min: 95, bucket: STATUS_KEY.good },
                          { min: 85, bucket: STATUS_KEY.info },
                          { min: 0,  bucket: STATUS_KEY.warning },
                        ])) }}
                      />
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5 text-right">{c.score}%</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </ChartCard>

          {/* Consent Management */}
          <ChartCard title="Consent Management Panel"
            subtitle="7 data categories · legal basis · consent count · withdrawals · PII flag">
            <div className="overflow-x-auto">
              <table className="gov-table w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left">{t("Data Category")}</th>
                    <th className="text-left">{t("Legal Basis")}</th>
                    <th className="text-center">{t("Consented")}</th>
                    <th className="text-center">{t("Withdrawn")}</th>
                    <th className="text-left">{t("Retention")}</th>
                    <th className="text-center">PII</th>
                    <th>{t("Purpose")}</th>
                  </tr>
                </thead>
                <tbody>
                  {CONSENT_RECORDS.map((r, i) => (
                    <motion.tr key={r.category} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                      <td className="font-bold text-slate-900">{t(r.category)}</td>
                      <td className="text-[9px] text-slate-500">{t(r.basis)}</td>
                      <td className="text-center font-black text-emerald-600">{r.consented.toLocaleString()}</td>
                      <td className="text-center font-bold text-amber-600">{r.withdrawn}</td>
                      <td className="text-[9px] text-slate-500">{t(r.retention)}</td>
                      <td className="text-center">
                        {r.pii
                          ? <span className="text-[8px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">PII</span>
                          : <span className="text-[8px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">{t("Non-PII")}</span>}
                      </td>
                      <td className="text-[9px] text-slate-500">{t(r.purpose)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Right to Erasure requests */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Right to Erasure Requests — Processed")}</div>
              <div className="space-y-1.5">
                {erasureRequests.map((e, i) => (
                  <div key={e.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 text-xs flex-wrap">
                    <span className="font-mono text-[9px] text-blue-700">{e.id}</span>
                    <span className="font-bold text-slate-800 flex-1">{t(e.subject)}</span>
                    <span className="text-slate-500">{t(e.type)}</span>
                    <span className="text-[9px] text-slate-400">{t("Filed")}: {e.filed}</span>
                    <span className="badge badge-success text-[8px]"><CheckCircle size={8} className="mr-0.5" />{t(e.status)} {t("in")} {e.days} {t("days")}</span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>

          {/* Data Retention */}
          <ChartCard title="Data Retention Dashboard"
            subtitle="8 data categories · retention period · auto-delete status · next purge date · storage volume">
            <div className="overflow-x-auto">
              <table className="gov-table w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left">{t("Data Category")}</th>
                    <th className="text-center">{t("Retention Period")}</th>
                    <th className="text-center">{t("Auto-Delete")}</th>
                    <th className="text-center">{t("Next Purge")}</th>
                    <th className="text-center">{t("Stored")}</th>
                    <th>{t("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {RETENTION_POLICY.map((r, i) => (
                    <motion.tr key={r.category} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                      <td className="font-bold text-slate-900">{t(r.category)}</td>
                      <td className="text-center font-semibold text-blue-700">{t(r.period)}</td>
                      <td className="text-center">
                        {r.autoDelete
                          ? <span className="badge badge-success text-[8px]">{t("Auto")}</span>
                          : <span className="badge badge-warning text-[8px]">{t("Manual")}</span>}
                      </td>
                      <td className="text-center text-[9px] text-slate-500">{t(r.nextPurge)}</td>
                      <td className="text-center font-bold text-slate-700">{r.stored}</td>
                      <td><span className={statusBadge(r.status)}>{t(r.status)}</span></td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>

          {/* Privacy-by-Design */}
          <ChartCard title="Privacy-by-Design Controls — Per Module"
            subtitle="Anonymisation · pseudonymisation · data minimisation · biometric storage policy per Maha Kumbh AI module">
            <div className="overflow-x-auto">
              <table className="gov-table w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left">{t("Module")}</th>
                    <th className="text-left">{t("Anonymisation")}</th>
                    <th className="text-left">{t("Pseudonymisation")}</th>
                    <th className="text-center">{t("Min. Data")}</th>
                    <th className="text-center">{t("Biometric Stored")}</th>
                    <th className="text-left">{t("Consent Basis")}</th>
                  </tr>
                </thead>
                <tbody>
                  {PRIVACY_BY_DESIGN.map((p, i) => (
                    <motion.tr key={p.module} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                      <td className="font-black text-slate-900">{t(p.module)}</td>
                      <td className="text-[9px] text-emerald-700">{t(p.anonymise)}</td>
                      <td className="text-[9px] text-blue-700">{t(p.pseudonym)}</td>
                      <td className="text-center"><CheckCircle size={12} className="text-emerald-500 mx-auto" /></td>
                      <td className="text-center">
                        {p.biometricStored
                          ? <span className="text-red-600 font-bold text-[10px]">⚠ {t("Yes")}</span>
                          : <span className="text-emerald-600 font-bold text-[10px]">✓ {t("No")}</span>}
                      </td>
                      <td className="text-[9px] text-slate-500">{t(p.consent)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </motion.div>
      )}

      {/* ── Responsible AI ── */}
      {activeTab === "responsibleAI" && (
        <motion.div key="rai" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} className="space-y-6">

          {/* 7 Principles expanded */}
          <div>
            <h3 className="text-sm font-black text-slate-900 mb-3">{t("7 Responsible AI Principles — Detailed Status")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {RESPONSIBLE_AI_PRINCIPLES.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div key={p.id}
                    initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                    className={`p-4 rounded-2xl border ${p.score === 100 ? `${STATUS.good.border} bg-emerald-50/20` : `${STATUS.info.border} bg-blue-50/10`}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Icon size={16} className="text-blue-700" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-xs font-black text-slate-900">{t(p.title)}</span>
                          <span className={statusBadge(p.status)}>{t(p.status)}</span>
                        </div>
                        <p className="text-[10px] text-slate-600 leading-relaxed">{t(p.desc)}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <motion.div className="h-1.5 rounded-full bg-emerald-500"
                              initial={{ width:0 }} animate={{ width:`${p.score}%` }} transition={{ duration:0.8 }} />
                          </div>
                          <span className="text-[10px] font-black text-emerald-600">{p.score}%</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
              {/* 7th principle gets a full-width card */}
            </div>
          </div>

          {/* AI Model Governance */}
          <ChartCard title="AI Model Governance Register"
            subtitle="8 active AI models · human-in-loop · explainability method · bias check · training data declaration">
            <div className="overflow-x-auto">
              <table className="gov-table w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left">{t("AI Model")}</th>
                    <th className="text-left">{t("Purpose")}</th>
                    <th className="text-center text-emerald-700">{t("Human Loop")}</th>
                    <th className="text-center">{t("Explainer")}</th>
                    <th className="text-center">{t("Conf. Threshold")}</th>
                    <th className="text-center">{t("Bias Check")}</th>
                    <th className="text-center">{t("Last Review")}</th>
                    <th className="text-left">{t("Vendor")}</th>
                  </tr>
                </thead>
                <tbody>
                  {AI_MODELS.map((m, i) => (
                    <motion.tr key={m.model} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                      <td className="font-black text-slate-900">{t(m.model)}</td>
                      <td className="text-[9px] text-slate-500">{t(m.purpose)}</td>
                      <td className="text-center">
                        <span className={`font-black text-[10px] ${m.humanLoop === 100 ? STATUS.good.text : STATUS.warning.text}`}>{m.humanLoop}%</span>
                      </td>
                      <td className="text-center">
                        <span className="text-[8px] font-black bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">{m.explainer}</span>
                      </td>
                      <td className="text-center font-semibold text-slate-700">{m.confThreshold}%</td>
                      <td className="text-center"><span className={statusBadge(m.biasCheck)}>{t(m.biasCheck)}</span></td>
                      <td className="text-center text-[9px] text-slate-400">{m.lastReview}</td>
                      <td className="text-[9px] text-blue-700 font-semibold">{m.vendor}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[9px] text-violet-600 font-bold mt-3 pt-2 border-t border-slate-100">
              {t("⚠ All AI models require human officer approval before any enforcement, punitive or access-denial action — DPDP 2023 · MeitY AI SOP 2023")}
            </p>
          </ChartCard>

          {/* Transparency Report */}
          <ChartCard title="AI Transparency Report — Monthly"
            subtitle="AI actions raised · human reviewed · officer overrides — published monthly">
            <div className="overflow-x-auto mb-4">
              <table className="gov-table w-full text-xs">
                <thead>
                  <tr>
                    <th>{t("Month")}</th>
                    <th className="text-center">{t("AI Actions")}</th>
                    <th className="text-center">{t("Human Reviewed")}</th>
                    <th className="text-center">{t("Officer Overrides")}</th>
                    <th className="text-center">{t("Review Rate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {TRANSPARENCY_REPORT.map((r, i) => (
                    <motion.tr key={r.month} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.07 }}>
                      <td className="font-bold text-slate-900">{t(r.month)}</td>
                      <td className="text-center font-semibold">{r.aiActions.toLocaleString()}</td>
                      <td className="text-center font-black text-emerald-600">{r.humanReviewed.toLocaleString()}</td>
                      <td className="text-center font-black text-amber-600">{r.overridden}</td>
                      <td className="text-center">
                        <span className={`font-black text-[11px] ${r.pct >= 100 ? STATUS.good.text : STATUS.warning.text}`}>{r.pct}%</span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 flex-wrap text-[9px]">
              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full font-bold">✓ {t("Published on MahaGov portal")}</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-bold">✓ {t("Available under RTI")}</span>
              <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-full font-bold">✓ {t("DPDP 2023 compliant")}</span>
            </div>
          </ChartCard>
        </motion.div>
      )}

      {/* ── Access & Audit ── */}
      {activeTab === "access" && (
        <motion.div key="access" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} className="space-y-6">

          {/* RBAC */}
          <ChartCard title="Role-Based Access Control (RBAC)"
            subtitle="6 roles · access level · users · permissions · 2FA enforcement">
            <div className="overflow-x-auto mb-4">
              <table className="gov-table w-full text-xs">
                <thead>
                  <tr>
                    <th>{t("Role")}</th><th>{t("Access Level")}</th><th className="text-center">{t("Users")}</th>
                    <th>{t("Permissions")}</th><th className="text-center">2FA</th>
                  </tr>
                </thead>
                <tbody>
                  {rbacRoles.map((r, i) => (
                    <motion.tr key={r.role} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                      <td className="font-bold text-slate-900">{t(r.role)}</td>
                      <td><span className="badge badge-info text-[9px]">{t(r.level)}</span></td>
                      <td className="text-center font-semibold">{r.users.toLocaleString()}</td>
                      <td className="text-slate-600 text-[10px]">{t(r.permissions)}</td>
                      <td className="text-center">
                        {r.twoFA
                          ? <span className="badge badge-success text-[8px]"><CheckCircle size={9} className="mr-0.5" />{t("Enforced")}</span>
                          : <span className="badge badge-warning text-[8px]">{t("Optional")}</span>}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Access Activity by Role — Today")}</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={governanceData.accessActivity} layout="vertical" margin={{ left:10, right:20 }}>
                    <XAxis type="number" tick={{ ...axisTick, fontSize:9 }} />
                    <YAxis type="category" dataKey="role" tick={{ ...axisTick, fontSize:9 }} width={100} tickFormatter={t} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={legendStyle} />
                    <Bar dataKey="logins"  fill={chartColor(0)} radius={[0,3,3,0]} name={t("Logins")} />
                    <Bar dataKey="actions" fill={chartColor(1)} radius={[0,3,3,0]} name={t("Actions")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Active Sessions — Live")}</div>
                <div className="space-y-1.5">
                  {SESSION_MONITOR.map((s, i) => (
                    <div key={s.user} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 text-xs flex-wrap">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-[7px] font-black text-blue-700">{s.user[0]}</span>
                      </div>
                      <div className="flex-1 min-w-[80px]">
                        <div className="font-black text-slate-900 text-[10px]">{t(s.user)}</div>
                        <div className="text-[8px] text-slate-400">{t(s.role)} · {s.ip}</div>
                      </div>
                      <div className="text-[8px] text-slate-500 flex-shrink-0">{s.lastActivity}</div>
                      {s.mfa
                        ? <span className="badge badge-success text-[7px]"><Lock size={7} className="mr-0.5" />MFA</span>
                        : <span className="badge badge-warning text-[7px]">{t("No MFA")}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Failed logins */}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[9px] font-black text-red-600 uppercase mb-2">{t("Failed Login Attempts — Today")}</div>
              <div className="space-y-1.5">
                {FAILED_LOGINS.map((f, i) => (
                  <div key={f.ip} className="flex items-center gap-3 p-2.5 rounded-xl border border-red-100 bg-red-50/20 text-xs flex-wrap">
                    <span className="font-mono text-[9px] text-red-600">{f.time}</span>
                    <span className="font-bold text-slate-700 flex-1">{t(f.user)}</span>
                    <span className="text-[9px] text-slate-500 font-mono">{f.ip}</span>
                    <span className="font-black text-red-600 text-[10px]">{f.attempts} {t("attempts")}</span>
                    <span className="badge badge-critical text-[8px]">{t(f.status)}</span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>

          {/* Audit Trail */}
          <ChartCard title="System Audit Trail — Immutable Log"
            subtitle="All operator and AI actions · DPDP 2023 compliant · 22,840 entries today">
            <div className="overflow-x-auto">
              <table className="gov-table w-full text-xs">
                <thead>
                  <tr><th>{t("Log ID")}</th><th>{t("User")}</th><th>{t("Role")}</th><th>{t("Action")}</th><th>{t("Resource")}</th><th>{t("Time")}</th></tr>
                </thead>
                <tbody>
                  {auditLogEntries.map((log, i) => (
                    <motion.tr key={log.id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
                      <td className="font-black text-blue-700 font-mono">{log.id}</td>
                      <td className="font-bold text-slate-900">{t(log.user)}</td>
                      <td><span className={`badge text-[8px] ${log.role === "System" ? "badge-purple" : "badge-navy"}`}>{t(log.role)}</span></td>
                      <td className="text-slate-700 text-[10px] max-w-xs">{t(log.action)}</td>
                      <td className="text-[9px] text-slate-500 font-mono">{log.resource}</td>
                      <td className="text-slate-400 text-[9px] flex items-center gap-1"><Clock size={8} />{log.time}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center justify-between text-[9px] text-slate-500 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full font-bold">✓ {t("Immutable — SHA-256 hash chain")}</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-bold">✓ {t("7-year retention enforced")}</span>
              </div>
              <span>{t("Showing")} {auditLogEntries.length} {t("of")} <strong className="text-slate-700">{governanceData.auditLogs.toLocaleString("en-IN")}</strong> {t("entries")}</span>
            </div>
          </ChartCard>
        </motion.div>
      )}

      {/* ── SOC / Cyber ── */}
      {activeTab === "soc" && (
        <motion.div key="soc" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} className="space-y-6">

          {/* SIEM trend + Category pie */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="SOC / SIEM — Event Trend" subtitle="Security events · alerts · blocked actions — hourly" live>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={SOC_EVENTS_TREND} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                  <defs>
                    <linearGradient id="eventGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="time" tick={{ ...axisTick, fontSize:9 }} />
                  <YAxis tick={{ ...axisTick, fontSize:9 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={legendStyle} />
                  <Area type="monotone" dataKey="events"  fill="url(#eventGrad)" stroke={chartColor(0)} strokeWidth={2} name={t("Events")} />
                  <Line type="monotone" dataKey="alerts"  stroke={chartColor(1)} strokeWidth={2} dot={{ r:3 }} name={t("Alerts")} />
                  <Bar               dataKey="blocked" fill={chartColor(2)} radius={[2,2,0,0]} name={t("Blocked")} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Event Category Breakdown" subtitle="Security event types today — all 10 events categorised">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={EVENT_CATEGORY_PIE} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" outerRadius={70} innerRadius={36}
                    label={({ name, value }) => `${t(name)}: ${value}`} labelLine={false}>
                    {EVENT_CATEGORY_PIE.map((e, i) => <Cell key={i} fill={chartColor(i)} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 grid grid-cols-2 gap-1">
                {EVENT_CATEGORY_PIE.map((e, i) => (
                  <div key={e.name} className="flex items-center gap-1.5 text-[9px]">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                    <span className="text-slate-600">{t(e.name)}: <strong>{e.value}</strong></span>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>

          {/* SIEM Event Log */}
          <ChartCard title="Cyber Incident Log — SIEM Feed"
            subtitle="10 security events today · all external intrusion attempts blocked" live>
            <div className="space-y-1.5">
              {SIEM_EXTENDED.map((e, i) => (
                <motion.div key={e.id}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.04 }}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${sevBg(e.sev)} flex-wrap`}
                >
                  <span className="font-mono text-[8px] text-slate-400 w-10 flex-shrink-0">{e.time}</span>
                  <span className="font-mono text-[8px] text-blue-700 flex-shrink-0">{e.id}</span>
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-[10px] font-bold text-slate-900">{t(e.type)}</div>
                    <div className="text-[9px] text-slate-500">{t(e.source)}</div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                    <span className="text-[8px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full font-bold">{t(e.category)}</span>
                    <span className={sevBadge(e.sev)}>{t(e.sev)}</span>
                    <span className={stBadge2(e.status)}>{t(e.status)}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </ChartCard>

          {/* Cyber Risk Analytics */}
          <ChartCard title="Cyber Risk Analytics — Attack Vector Assessment"
            subtitle="6 attack vectors · inherent risk · incidents · mitigated · residual risk score">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-2.5">
                {CYBER_RISK.map((r, i) => (
                  <motion.div key={r.vector}
                    initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
                    className="p-3 rounded-xl border border-slate-200"
                  >
                    <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                      <span className="text-xs font-black text-slate-900">{t(r.vector)}</span>
                      <div className="flex items-center gap-2 text-[9px]">
                        <span className="text-slate-500">{t("Incidents")}: <strong className="text-slate-700">{r.incidents}</strong></span>
                        <span className={`font-black px-1.5 py-0.5 rounded-full ${
                          r.residual >= 20 ? `${STATUS.warning.bg} ${STATUS.warning.text}` : `${STATUS.good.bg} ${STATUS.good.text}`
                        }`}>{t("Residual")}: {r.residual}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden relative">
                        <motion.div className="absolute inset-y-0 left-0 rounded-full bg-red-100"
                          initial={{ width:0 }} animate={{ width:`${r.risk}%` }} transition={{ duration:0.8 }} />
                        <motion.div className="absolute inset-y-0 left-0 rounded-full bg-emerald-500"
                          initial={{ width:0 }} animate={{ width:`${(r.mitigated/Math.max(r.risk,1))*100*0.7}%` }} transition={{ duration:0.9 }} />
                      </div>
                      <span className="text-[9px] font-black text-red-600 flex-shrink-0">{t("Risk")}: {r.risk}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={CYBER_RISK} layout="vertical" margin={{ left:10, right:30, top:5, bottom:5 }}>
                    <CartesianGrid {...gridProps} horizontal={false} />
                    <XAxis type="number" tick={{ ...axisTick, fontSize:9 }} />
                    <YAxis type="category" dataKey="vector" tick={{ ...axisTick, fontSize:8 }} width={120} tickFormatter={t} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={legendStyle} />
                    <Bar dataKey="risk"      fill={chartColor(0)} radius={[0,3,3,0]} name={t("Inherent Risk")} />
                    <Bar dataKey="residual"  fill={chartColor(1)} radius={[0,3,3,0]} name={t("Residual Risk")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ChartCard>
        </motion.div>
      )}

      {/* ── Data Sovereignty & Bhasini ── */}
      {activeTab === "sovereignty" && (
        <motion.div key="sov" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} className="space-y-6">

          {/* Sovereignty Banner */}
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
            className="rounded-2xl bg-gradient-to-r from-orange-600 via-amber-400 to-green-600 p-5 text-white">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🇮🇳</span>
              <span className="text-[10px] font-black text-white tracking-widest">
                {t("DATA SOVEREIGNTY — ALL DATA WITHIN INDIA · NIC CERTIFIED · DPDP 2023 COMPLIANT")}
              </span>
            </div>
            <p className="text-sm font-black text-white mb-1">{t("India's Data — Stays in India")}</p>
            <p className="text-xs text-white/80">
              {t("All Maha Kumbh AI / Maha AI data stored exclusively on NIC cloud infrastructure within India · No cross-border data transfer without MHA clearance · STQC certified · CERT-In compliant · Zero foreign cloud dependency for citizen PII")}
            </p>
          </motion.div>

          {/* Sovereignty Infrastructure */}
          <ChartCard title="Data Sovereignty Infrastructure"
            subtitle="NIC cloud · STQC certification · MeitY compliance · geo-fencing · all components India-hosted">
            <div className="space-y-2">
              {SOVEREIGNTY_INFRA.map((s, i) => (
                <motion.div key={s.component}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                  className="flex items-center gap-4 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors flex-wrap"
                >
                  <div className="w-8 h-8 rounded-lg gov-gradient flex items-center justify-center flex-shrink-0">
                    <Server size={14} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-xs font-black text-slate-900">{t(s.component)}</span>
                      <span className="text-[8px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-bold">{t(s.type)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] text-slate-500 flex-wrap">
                      <Globe size={9} />{t(s.location)}
                      <span>· {t(s.compliance)}</span>
                      <span>· {t("Cert")}: {s.certExpiry}</span>
                    </div>
                  </div>
                  <span className={statusBadge(s.status)}>{t(s.status)}</span>
                </motion.div>
              ))}
            </div>
          </ChartCard>

          {/* Data Residency Legal Framework */}
          <ChartCard title="Data Residency — Legal Compliance Framework"
            subtitle="6 applicable laws · requirement · compliance status · verification mechanism">
            <div className="overflow-x-auto">
              <table className="gov-table w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left">{t("Framework")}</th>
                    <th className="text-left">{t("Requirement")}</th>
                    <th>{t("Status")}</th>
                    <th className="text-left">{t("Verification")}</th>
                  </tr>
                </thead>
                <tbody>
                  {DATA_RESIDENCY.map((r, i) => (
                    <motion.tr key={r.law} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}>
                      <td className="font-black text-slate-900">{t(r.law)}</td>
                      <td className="text-[10px] text-slate-600">{t(r.requirement)}</td>
                      <td><span className={statusBadge(r.status)}>{t(r.status)}</span></td>
                      <td className="text-[9px] text-emerald-700 font-semibold">{t(r.verification)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>

          {/* Bhasini */}
          <div className="rounded-2xl border-2 border-violet-200 overflow-hidden">
            <div className="bg-gradient-to-r from-violet-900 via-purple-900 to-blue-900 px-5 py-4 text-white">
              <div className="flex items-center gap-2 mb-1">
                <Globe size={16} className="text-violet-300" />
                <span className="text-[10px] font-black text-white tracking-widest">
                  {t("BHASINI — MeitY NATIONAL LANGUAGE TRANSLATION MISSION · RESPONSIBLE MULTILINGUAL AI")}
                </span>
              </div>
              <h3 className="text-sm font-black text-white">भाषिणी — {t("Multilingual AI Module")}</h3>
              <p className="text-xs text-white/80 mt-1">
                {t("10 Indian languages · constitutional obligation for linguistic equality · Maha Kumbh AI advisories broadcast in all supported languages via Bhasini API · Responsible AI bias testing per language · Human review before critical advisories")}
              </p>
            </div>

            <div className="p-5 space-y-4 bg-white">
              {/* Language coverage table */}
              <div>
                <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Language Coverage — Bhasini Integration")}</div>
                <div className="overflow-x-auto">
                  <table className="gov-table w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-left">{t("Language")}</th>
                        <th className="text-left">{t("Script")}</th>
                        <th className="text-center">{t("Accuracy")}</th>
                        <th className="text-center">{t("Advisories")}</th>
                        <th className="text-center">{t("Helpline Q.")}</th>
                        <th>{t("AI Model")}</th>
                        <th className="text-center">{t("Bias Check")}</th>
                        <th>{t("Status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {BHASINI_LANGS.map((l, i) => (
                        <motion.tr key={l.lang} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.04 }}>
                          <td className="font-black text-slate-900">{t(l.lang)}</td>
                          <td className="text-[9px] text-slate-500">{t(l.script)}</td>
                          <td className="text-center">
                            <span className={`font-black text-[10px] ${STATUS[thresholdStatus(l.accuracy, [
                              { min: 90, bucket: STATUS_KEY.good },
                              { min: 80, bucket: STATUS_KEY.warning },
                              { min: 0,  bucket: STATUS_KEY.critical },
                            ])].text}`}>{l.accuracy}%</span>
                          </td>
                          <td className="text-center font-semibold">{l.advisory}</td>
                          <td className="text-center font-semibold">{l.helpline.toLocaleString()}</td>
                          <td className="text-[9px] text-blue-700 font-semibold">{l.aiModel}</td>
                          <td className="text-center"><span className={statusBadge(l.bias)}>{t(l.bias)}</span></td>
                          <td>
                            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${
                              l.status === "Primary" ? `${STATUS.info.bg} ${STATUS.info.text}` :
                              l.status === "Active"  ? `${STATUS.good.bg} ${STATUS.good.text}` : `${STATUS.neutral.bg} ${STATUS.neutral.text}`
                            }`}>{t(l.status)}</span>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bhasini Responsible AI checklist */}
              <div>
                <div className="text-[9px] font-black text-slate-500 uppercase mb-2">{t("Bhasini Responsible AI Checklist")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {BHASINI_RESPONSIBLE_AI.map((b, i) => (
                    <motion.div key={b.check}
                      initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
                      className={`flex items-start gap-2 p-3 rounded-xl border ${
                        b.status === "Pass" || b.status === "Enforced" || b.status === "Active"
                          ? `${STATUS.good.border} bg-emerald-50/20`
                          : `${STATUS.warning.border} bg-amber-50/20`
                      }`}
                    >
                      <span className={`text-[10px] font-black flex-shrink-0 mt-0.5 ${
                        b.status === "Pass" || b.status === "Enforced" ? STATUS.good.text :
                        b.status === "Active" ? STATUS.info.text : STATUS.warning.text
                      }`}>
                        {b.status === "Pass" || b.status === "Enforced" ? "✓" : b.status === "Active" ? "●" : "⚠"}
                      </span>
                      <div>
                        <div className="text-[10px] font-black text-slate-800">{t(b.check)}</div>
                        <div className="text-[9px] text-slate-500 mt-0.5">{t(b.note)}</div>
                      </div>
                      <span className={`badge text-[8px] ml-auto flex-shrink-0 ${statusBadge(b.status)}`}>{t(b.status)}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap text-[9px] pt-2 border-t border-slate-100">
                <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-full font-bold">Bhasini API v2.1</span>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full font-bold">99.6% {t("Uptime")}</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-bold">{t("MeitY Certified")}</span>
                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full font-bold">{t("Constitutional Art. 343")}</span>
                <span className="text-slate-500">{t("⚠ Human review mandatory for all critical safety advisories before multilingual broadcast")}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}





